import React from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useBasePath } from '../../../../lib/use-base-path';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import {
  claimLandedCreditMinerRender,
  CREDIT_MINER_BOX,
  type CreditMinerGame,
  fetchCreditMinerProgress,
  freshestCreditMinerProgress,
  hasSaveInThisTab,
  landedKey,
  type LandedResult,
  loadCreditMiner,
  loadCreditMinerStyles,
  onLanded,
  readLanded,
  rememberedBankedMicro,
  saveCreditMinerProgress,
  parkCreditMinerRender,
  rememberVeilLifted,
  veilLiftedOnThisPage,
} from '../../../../lib/credit-miner';
import type { AttachmentPalette } from './palette';

/**
 * The image-render placeholder as a game. It takes the box the picture will land in and runs the Credit
 * Miner there until the attachment resolves.
 *
 * Every unmount parks the render (`parkCreditMinerRender`: the bag is banked and the save flushed) and then
 * tries to claim it (`claimLandedCreditMinerRender`) — whether this unmount is the chat remounting the row
 * mid-render or the picture genuinely landing. The two look identical from here (the game outlives the fill
 * until the picture has decoded, so there is no local signal that reliably tells them apart at the exact
 * unmount instant), and the harness already refuses a claim against a render it has not recorded as
 * delivered — `claimLandedCreditMinerRender` itself retries until it accepts one or the render's park window
 * lapses, so there is nothing for a client-side guess at "delivered" to add except a way to drop a real
 * claim by guessing wrong. The claim result reaches `MinedCreditsBadge` through the landed buffer.
 */
export function CreditMinerPlaceholder({
  att,
  conversationId,
  messageId,
  palette,
}: {
  att: MessageAttachment;
  conversationId?: string;
  messageId?: string;
  palette: AttachmentPalette;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const basePath = useBasePath();
  const [ready, setReady] = React.useState(false);
  const [stylesReady, setStylesReady] = React.useState(false);
  // A tap on the stand-in's play button before the game has loaded: kept, and the game starts on it once ready.
  const [playRequested, setPlayRequested] = React.useState(false);
  const playRequestedRef = React.useRef(false);
  const requestPlay = React.useCallback(() => {
    playRequestedRef.current = true;
    setPlayRequested(true);
  }, []);
  React.useEffect(() => {
    let live = true;
    void loadCreditMinerStyles(basePath).then(() => live && setStylesReady(true));
    return () => {
      live = false;
    };
  }, [basePath]);
  useLoadBi('credit-miner', { isLoading: !ready });
  // Engagement, in three generic events: OFFERED (the game stood in the placeholder), PLAYED (the veil was
  // lifted), BOUGHT (a gear purchase). The money side is reported by the server at claim time, where the
  // amount is the one that was actually paid.
  const render = landedKey(messageId);
  const entity = conversationId ? { id: conversationId, type: ENTITY_TYPE.conversation } : undefined;
  const seenRef = useItemSeenBi('credit-miner', { entity, additionalFields: { render } });
  const { logPageClickBi } = useGenericBi();
  const onLog = React.useCallback(
    (kind: string, _text: string, data?: Record<string, unknown>) => {
      if (kind === 'play') {
        rememberVeilLifted(render);
        logPageClickBi({ page_name: 'chat', button_name: 'credit_miner_play', conversation_id: conversationId }, { entity, additionalFields: { render } });
      } else if (kind === 'buy') {
        logPageClickBi(
          { page_name: 'chat', button_name: 'credit_miner_buy', conversation_id: conversationId, action: String(data?.id ?? '') },
          { entity, additionalFields: { render, gear: String(data?.id ?? ''), level: String(data?.level ?? '') } },
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId, render],
  );
  const setRoot = React.useCallback(
    (node: HTMLDivElement | null) => {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      seenRef(node);
    },
    [seenRef],
  );

  const game = React.useRef<CreditMinerGame | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [create, firstRead] = await Promise.all([loadCreditMiner(basePath), fetchCreditMinerProgress()]);
      let progress = firstRead;
      // A read that did not come back is not an empty save. With this tab's own save to resume from, the game
      // starts on it at once; with nothing to resume from it would be a fresh mine that could never save, so
      // the shimmer stays up while slower reads get their chance.
      for (const timeoutMs of SLOW_READ_TIMEOUTS_MS) {
        if (progress || hasSaveInThisTab() || cancelled) break;
        progress = await fetchCreditMinerProgress(timeoutMs);
      }
      if (cancelled || !ref.current || (!progress && !hasSaveInThisTab())) return;
      const bankedMicro = progress?.bankedMicro ?? rememberedBankedMicro();
      game.current = create(ref.current, {
        // The message that carries the placeholder is the render: a new message is a new render, and the
        // per-render credit cap resets with it.
        renderId: landedKey(messageId),
        veil: !veilLiftedOnThisPage(landedKey(messageId)),
        balance: bankedMicro === null ? undefined : { bankedMicro },
        storageKey: null,
        storage: {
          load: () => freshestCreditMinerProgress(progress?.progress ?? null, progress?.resetAt ?? null),
          save: saveCreditMinerProgress,
        },
        onLog,
      });
      if (playRequestedRef.current) game.current.play();
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // The game runs for the life of this placeholder; nothing here should restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Parked in a LAYOUT cleanup: it runs before the picture that replaces us is painted, so the picture can
  // fade in over the game's last frame rather than flash the image shimmer first.
  React.useLayoutEffect(() => {
    const node = ref.current;
    return () => {
      if (game.current) parkCreditMinerRender(game.current, landedKey(messageId), conversationId, lastFrameOf(node));
      game.current = null;
      claimLandedCreditMinerRender(landedKey(messageId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Until the game starts, the box already shows its opening screen, drawn with the game's own stylesheet: a new
  // picture always opens on it, so the player sees one loading screen, not a generic one and then the game's. The
  // game owns everything inside its root, so this stand-in is a sibling.
  return (
    <div
      aria-label={att.fileName}
      aria-busy="true"
      style={{
        position: 'relative',
        width: CREDIT_MINER_BOX.w,
        height: CREDIT_MINER_BOX.h,
        borderRadius: 8,
        overflow: 'hidden',
        background: palette.tileBg,
        border: palette.tileBorder,
      }}
    >
      <div ref={setRoot} style={{ width: '100%', height: '100%' }} />
      {!ready && stylesReady && (
        <OpeningScreen resumed={veilLiftedOnThisPage(landedKey(messageId))} playRequested={playRequested} onPlay={requestPlay} />
      )}
    </div>
  );
}

/** Two slower tries after the first read times out: a harness answer has been measured taking 28 s. */
const SLOW_READ_TIMEOUTS_MS = [10_000, 20_000];

/** The game's own opening screen, from its stylesheet, for the moment before the game has started. A render the
 *  player already started on this page resumes without it, so that case keeps just the game's dark box. */
function OpeningScreen({ resumed, playRequested, onPlay }: { resumed: boolean; playRequested: boolean; onPlay: () => void }) {
  const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  return (
    <div className="cm-box cm-veiled" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 0 }}>
      {!resumed && (
        <div className="cm-intro" onPointerDown={onPlay}>
          <div className="cm-intro-title">
            <i className="cm-spin" />
            Rendering your image
          </div>
          <div className="cm-intro-sub">Mine Symphony credits while you wait</div>
          <div className="cm-intro-cta">
            {playRequested && <i className="cm-spin" style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />}
            {coarse ? 'Tap to play' : 'Click to play'}
          </div>
        </div>
      )}
    </div>
  );
}

/** The mine as it last looked, for the picture to fade in over. A tainted or detached canvas gives nothing. */
function lastFrameOf(node: HTMLDivElement | null): string | undefined {
  try {
    return node?.querySelector('canvas')?.toDataURL('image/png') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The credits this render mined, rising over the picture that just landed and fading out. Says "mined"
 * from the game's own number at once, and "added to your balance" once the server reports a whole credit
 * landed — the two moments are seconds apart and the second one is the one that moves the sidebar.
 */
export function MinedCreditsBadge({ messageId }: { messageId?: string }) {
  const key = landedKey(messageId);
  const [result, setResult] = React.useState<LandedResult | undefined>(() => readLanded(key));
  const [shown, setShown] = React.useState(false);
  const [gone, setGone] = React.useState(false);
  React.useEffect(() => onLanded(key, setResult), [key]);
  React.useEffect(() => {
    if (!result) return;
    const up = setTimeout(() => setShown(true), 350);
    const out = setTimeout(() => setGone(true), 4200);
    return () => {
      clearTimeout(up);
      clearTimeout(out);
    };
  }, [result]);
  if (!result || gone) return null;
  const credits = result.minedMicro / 1_000_000;
  const text = result.wholeCredits ? `${result.wholeCredits} credit${result.wholeCredits === 1 ? '' : 's'} added to your balance` : `+${credits.toFixed(credits < 0.01 ? 3 : 2)} credits mined`;
  return (
    <span
      aria-live="polite"
      style={{
        position: 'absolute',
        left: 10,
        bottom: 10,
        padding: '5px 10px',
        borderRadius: 999,
        background: 'var(--ds-lime-400)',
        color: 'var(--ds-black)',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        boxShadow: '0 4px 14px rgba(var(--ds-black-rgb), 0.35)',
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity .35s ease, transform .35s ease',
        pointerEvents: 'none',
      }}
    >
      {text}
    </span>
  );
}
